import { registerSW } from 'virtual:pwa-register';

const LEGACY_SW_FILENAME = 'service-worker.js';
const CLEANUP_RELOAD_KEY = 'fulbito-legacy-sw-cleanup';

function isLegacyServiceWorker(scriptUrl: string | undefined): boolean {
  return Boolean(scriptUrl?.includes(LEGACY_SW_FILENAME));
}

async function clearCaches(): Promise<void> {
  if (!('caches' in window)) {
    return;
  }

  const names = await caches.keys();
  await Promise.all(names.map((name) => caches.delete(name)));
}

async function unregisterLegacyRegistrations(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) {
    return false;
  }

  const registrations = await navigator.serviceWorker.getRegistrations();
  const legacyRegistrations = registrations.filter((registration) => {
    const scriptUrl =
      registration.active?.scriptURL ??
      registration.waiting?.scriptURL ??
      registration.installing?.scriptURL;
    return isLegacyServiceWorker(scriptUrl);
  });

  if (legacyRegistrations.length === 0) {
    return false;
  }

  await Promise.all(legacyRegistrations.map((registration) => registration.unregister()));
  await clearCaches();
  return true;
}

async function legacyServiceWorkerExists(swUrl: string): Promise<boolean> {
  const response = await fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  });
  const contentType = response.headers.get('content-type');
  return response.ok && Boolean(contentType?.includes('javascript'));
}

/** Remove stale Workbox SW left over from earlier PWA builds. */
export async function purgeLegacyAppServiceWorker(): Promise<void> {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator) || !window.isSecureContext) {
    return;
  }

  const swUrl = `${import.meta.env.BASE_URL}${LEGACY_SW_FILENAME}`;

  try {
    const hasLegacyBundle = await legacyServiceWorkerExists(swUrl);
    if (hasLegacyBundle) {
      return;
    }

    const removedLegacy = await unregisterLegacyRegistrations();
    if (!removedLegacy || sessionStorage.getItem(CLEANUP_RELOAD_KEY)) {
      return;
    }

    sessionStorage.setItem(CLEANUP_RELOAD_KEY, '1');
    window.location.reload();
  } catch {
    // Offline or blocked request — app should still render from network cache.
  }
}

/** Register the Vite PWA service worker (production, secure contexts only). */
export function registerAppServiceWorker(): void {
  if (!import.meta.env.PROD || !window.isSecureContext) {
    return;
  }

  registerSW({
    immediate: true,
    onRegisterError(error) {
      console.error('Service worker registration failed:', error);
    },
  });
}

export async function initServiceWorker(): Promise<void> {
  await purgeLegacyAppServiceWorker();
  registerAppServiceWorker();
}

/** @deprecated Use initServiceWorker instead. */
export function register(): void {
  void initServiceWorker();
}

export function unregister(): void {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => registration.unregister())
      .catch((error) => {
        console.error(error.message);
      });
  }
}
